/**
 * propertyDataMapper.js
 * 
 * Mapper centralisé pour gérer l'ancien ET le nouveau format de données
 * Assure la compatibilité entre les biens créés avant/après la refonte
 */

import { formatPriceFR, formatRentFR } from './formatFR';

/**
 * Obtenir le droit d'entrée (pas-de-porte) pour location_droit_entree
 * @param {Object} property - Bien complet
 * @returns {number|null} - Montant du droit d'entrée ou null
 */
export function getDroitEntree(property) {
  if (!property) return null;
  const infoBail = property.informations_bail || {};
  return infoBail.droit_entree || null;
}

/**
 * Obtenir le prix à afficher selon l'opération
 * @param {Object} property - Bien complet
 * @returns {number|null} - Prix en euros ou null
 */
export function getPrixValue(property) {
  if (!property) return null;
  
  const operation = property.taxonomy?.operation || property.operation;
  const infoVente = property.informations_vente || {};
  const infoBail = property.informations_bail || {};
  const honoraires = property.honoraires_calcul || {};
  
  // iter130 — CASCADE UNIQUE pour les ventes : `informations_vente` est la source de
  // vérité (prix_cession = prix FAI calculé, puis net_vendeur = donnée pivot).
  // `prix_annonce` (prix marketing) est EXCLU : il masquait le vrai prix du bien.
  // `honoraires_calcul` n'est qu'un repli legacy. Miroir back : server.py::resolve_prop_prix
  if (operation === 'cession' || operation === 'vente_immo'
      || operation === 'parts_sociales' || operation === 'licence') {
    return infoVente.prix_cession || infoVente.net_vendeur || honoraires.prix_vente || null;
  }
  
  // Location avec pas-de-porte : prix principal = LOYER (pas le droit d'entrée)
  if (operation === 'location_droit_entree') {
    return infoBail.loyer_mensuel || (infoBail.loyer_annuel ? infoBail.loyer_annuel / 12 : null) || null;
  }
  
  if (operation === 'location') {
    // Pour affichage unique, retourner le loyer mensuel (ou annuel/12)
    return infoBail.loyer_mensuel || (infoBail.loyer_annuel ? infoBail.loyer_annuel / 12 : null) || honoraires.loyer_mensuel || null;
  }
  
  if (operation === 'location_gerance') {
    return infoBail.redevance_mensuelle || (infoBail.redevance_annuelle ? infoBail.redevance_annuelle / 12 : null) || null;
  }
  
  // Fallback : chercher dans honoraires_calcul (ancien format)
  return honoraires.prix_vente || null;
}

/**
 * Obtenir le libellé du prix selon l'opération
 * @param {Object} property
 * @returns {string} - Ex: "Prix de cession", "Net vendeur", "Loyer mensuel"
 */
export function getPrixLabel(property) {
  const operation = property.taxonomy?.operation || property.operation;
  
  if (operation === 'cession') {
    return 'Prix de cession';
  }
  
  if (operation === 'vente_immo') {
    return 'Net vendeur';
  }
  
  if (operation === 'location' || operation === 'location_droit_entree') {
    return 'Loyer mensuel';
  }
  
  if (operation === 'location_gerance') {
    return 'Redevance mensuelle';
  }
  
  return 'Prix';
}

/**
 * Formater le prix pour affichage (avec espaces et symbole)
 * @param {Object} property
 * @returns {string} - Ex: "250 000 €", "4 200 €/mois"
 */
export function formatPrixDisplay(property) {
  const prix = getPrixValue(property);
  if (!prix) return 'N/C';
  
  const operation = property.taxonomy?.operation || property.operation;
  
  if (operation === 'location' || operation === 'location_gerance' || operation === 'location_droit_entree') {
    return formatRentFR(prix);
  }
  
  // Vente/Cession : Format avec espaces
  return formatPriceFR(prix);
}

/**
 * Obtenir la surface totale
 * @param {Object} property
 * @returns {number|null}
 */
export function getSurfaceValue(property) {
  if (!property) return null;
  
  const comp = property.composition || {};
  
  // Nouveau format
  if (comp.superficie_totale) return comp.superficie_totale;
  if (comp.surface_totale) return comp.surface_totale;
  if (comp.surface_vente) return comp.surface_vente;
  
  return null;
}

/**
 * Obtenir le label formaté de la surface
 * @param {Object} property
 * @returns {string}
 */
export function getSurfaceLabel(property) {
  const surface = getSurfaceValue(property);
  if (!surface) return 'N/C';
  return `${surface} m²`;
}

/**
 * Obtenir la ville
 * @param {Object} property
 * @returns {string}
 */
export function getVille(property) {
  if (!property) return 'Ville NC';
  
  const publicLoc = property.public_location || {};
  const confLoc = property.confidential_location || {};
  const infoConf = property.informations_confidentielles || {};
  const localisation = property.localisation || {};
  const bg = property.bien_general || {};
  
  // Prioriser public_location / confidential_location (clé `city`/`city_label` OU `ville`)
  if (publicLoc.ville || publicLoc.city || publicLoc.city_label) return publicLoc.ville || publicLoc.city || publicLoc.city_label;
  if (confLoc.ville || confLoc.city) return confLoc.ville || confLoc.city;
  
  // Format standard
  if (infoConf.ville) return infoConf.ville;
  if (localisation.ville) return localisation.ville;
  
  // Fallback: adresse imbriquée (biens importés)
  const addr = infoConf.adresse;
  if (addr && typeof addr === 'object' && addr.ville) return addr.ville;
  
  if (bg.ville_generale) return bg.ville_generale;
  
  return 'Ville NC';
}

/**
 * Obtenir le secteur
 * @param {Object} property
 * @returns {string}
 */
export function getSecteur(property) {
  if (!property) return 'Secteur NC';
  
  const publicLoc = property.public_location || {};
  const bg = property.bien_general || {};
  
  if (publicLoc.sector_label) return publicLoc.sector_label;
  if (publicLoc.secteur) return publicLoc.secteur;
  if (bg.secteur) return bg.secteur;
  
  return 'Secteur NC';
}

/**
 * Obtenir le type de bien
 * @param {Object} property
 * @returns {string}
 */
/**
 * Obtenir le label et style de la transaction/opération
 * @param {Object} property
 * @returns {Object} {label, bgColor, icon}
 */
export function getTransactionBadge(property) {
  if (!property) return { label: 'N/C', bgColor: 'bg-slate-100', textColor: 'text-slate-700', icon: 'bi-building' };

  const operation = property.operation;

  // Pattern "soft badge" (révisé 06/06/2026) : fond pastel (stop 50/100) + texte
  // foncé (stop 700) de la même teinte, SANS bordure. Évite la collision perceptive
  // avec l'IA (ex-indigo-600) et la confusion CTA/badge (ex-fonds pleins vifs).
  const badges = {
    'location': { label: 'Location', bgColor: 'bg-sky-50', textColor: 'text-sky-700', icon: 'bi-key-fill' },
    'location_droit_entree': { label: 'Location + pas-de-porte', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700', icon: 'bi-cash-stack' },
    'location_gerance': { label: 'Loc-gérance', bgColor: 'bg-slate-100', textColor: 'text-slate-700', icon: 'bi-shop' },
    'cession': { label: 'Cession', bgColor: 'bg-teal-50', textColor: 'text-teal-700', icon: 'bi-tag-fill' },
    'vente_immo': { label: 'Vente', bgColor: 'bg-blue-50', textColor: 'text-blue-700', icon: 'bi-house-door-fill' }
  };

  return badges[operation] || { label: 'N/C', bgColor: 'bg-slate-100', textColor: 'text-slate-700', icon: 'bi-building' };
}

/**
 * Obtenir le titre métier lisible du bien
 * Utilise taxonomyConfig.json pour les labels
 * @param {Object} property
 * @returns {string}
 */
export function getTitreMetier(property) {
  if (!property) return 'Type NC';
  
  const operation = property.operation || property.taxonomy?.operation || 'cession';
  const objet = property.objet || property.taxonomy?.objet;
  
  if (!objet) {
    // Fallback: utiliser le profil si objet absent (biens importés)
    const profil = property.profil || property.taxonomy?.profil;
    if (profil) {
      const profilLabels = {
        'chr': 'CHR',
        'retail': 'Commerce de détail',
        'alimentation': 'Alimentation',
        'services_reglementes': 'Services réglementés',
        'services': 'Services',
        'tertiaire': 'Tertiaire',
        'industrie': 'Industrie'
      };
      return profilLabels[profil] || profil.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Type NC';
  }
  
  // Charger taxonomyConfig (import statique en haut du fichier nécessaire)
  // Pour l'instant, mapping manuel des cas les plus courants
  const labelMap = {
    'fonds_de_commerce': 'Fonds de commerce',
    'droit_au_bail': 'Droit au bail',
    'parts_sociales': 'Parts sociales',
    'licence_exploitation': "Licence d'exploitation",
    'local_commercial': operation === 'vente_immo' ? 'Local commercial (murs)' : 'Local commercial',
    'bureaux': 'Bureaux',
    'local_activite': "Local d'activité / entrepôt",
    'immeuble': 'Immeuble',
    'terrain': 'Terrain',
    'murs_commerciaux': 'Murs commerciaux'
  };
  
  return labelMap[objet] || objet.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Obtenir l'activité métier lisible
 * @param {Object} property
 * @returns {string}
 */
export function getActiviteMetier(property) {
  if (!property) return 'N/C';
  
  const profil = property.profil || property.taxonomy?.profil;
  const sous_profil = property.sous_profil || property.taxonomy?.sous_profil;
  
  // Labels métier pour profils
  const profilLabels = {
    'chr': 'CHR / hôtellerie-restauration',
    'retail': 'Commerce de détail (retail)',
    'alimentation': 'Alimentation', // legacy — code retiré de la taxo (v3), fallback conservé
    'services_reglementes': 'Services réglementés',
    'services': 'Services non réglementés',
    'production_reparation': 'Production / réparation / artisanat',
    'tertiaire': 'Tertiaire',
    'erp_public': 'ERP / Public',
    'industriel': 'Industriel',
    'logistique': 'Logistique',
    'investissement': 'Investissement',
    'licence': 'Licence'
  };
  
  // Labels métier pour sous-profils
  const sousProfilLabels = {
    'restaurant': 'Restaurant',
    'brasserie': 'Brasserie',
    'snack_fast_food': 'Snack / fast-food',
    'bar_cafe': 'Bar / café',
    'hotel': 'Hôtel',
    'hotel_meuble': 'Hôtel meublé',
    'pret_a_porter': 'Prêt-à-porter',
    'beaute_esthetique': 'Beauté / esthétique',
    'equipement_maison': 'Équipement de la maison',
    'bijouterie': 'Bijouterie / horlogerie',
    'boulangerie_patisserie': 'Boulangerie / pâtisserie',
    'epicerie': 'Épicerie',
    'primeur': 'Primeur',
    'boucherie': 'Boucherie / traiteur',
    'tabac_presse_pmu': 'Tabac / presse / PMU / Loto',
    'pharmacie': 'Pharmacie / parapharmacie',
    'optique': 'Optique',
    'auto_ecole': 'Auto-école',
    'coiffure': 'Coiffure',
    'pressing': 'Pressing',
    'agence_services': 'Agence / services',
    // Nouvelle catégorie Production / réparation / artisanat (v3)
    'garage_carrosserie': 'Garage / carrosserie',
    'reparation': 'Réparation (cordonnerie, électronique, multiservices)',
    'imprimerie_repro': 'Imprimerie / reprographie',
    'menuiserie_ebenisterie': 'Menuiserie / ébénisterie',
    'serrurerie_metallerie': 'Serrurerie / métallerie',
    'atelier_fabrication': 'Atelier / fabrication artisanale',
    'open_space': 'Open space',
    'cloisonne': 'Cloisonné',
    'mixte': 'Mixte',
    'coworking': 'Coworking',
    'medical': 'Médical',
    'formation': 'Formation',
    'administration': 'Administration',
    'production': 'Production',
    'atelier': 'Atelier',
    'stockage': 'Stockage',
    'messagerie': 'Messagerie',
    'cross_dock': 'Cross-docking',
    'froid': 'Froid',
    'mono_locataire': 'Mono-locataire',
    'multi_locataires': 'Multi-locataires',
    'salle_sport': 'Salle de sport',
    // iter109 — Services libéraux (cabinets)
    'cabinet_medical': 'Cabinet médical',
    'cabinet_dentaire': 'Cabinet dentaire',
    'cabinet_paramedical': 'Cabinet paramédical',
    'laboratoire_analyses': 'Laboratoire d\'analyses',
    'cabinet_avocat': 'Cabinet d\'avocat',
    'cabinet_notaire': 'Étude notariale',
    'cabinet_comptable': 'Cabinet comptable',
    'cabinet_architecte': 'Cabinet d\'architecte',
    'agence_immobiliere': 'Agence immobilière',
    'cabinet_assurance': 'Cabinet d\'assurance',
    // iter109 — Services non réglementés (esthétique, salons)
    'esthetique': 'Esthétique / institut de beauté',
    'salon_beaute': 'Salon de beauté',
    'salon_the_cafe': 'Salon de thé / café',
    // iter109 — Restauration détaillée
    'restaurant_traditionnel': 'Restaurant traditionnel',
    'restaurant_gastronomique': 'Restaurant gastronomique',
    'restaurant_ambulant': 'Restaurant ambulant',
    'bar_brasserie': 'Bar / brasserie',
    'food_truck': 'Food truck',
    'licence_iii': 'Licence III',
    'licence_iv': 'Licence IV',
    // legacy — codes retirés de la taxo v3 (mais fallback conservé si un ancien bien les porte)
    'licence_restaurant': 'Licence restaurant',
    'petite_restauration': 'Petite restauration'
  };
  
  // Capitalise la 1re lettre (transformation d'affichage — la valeur DB reste inchangée).
  // On ne touche PAS au reste (préserve sigles / accents / minuscules internes voulues).
  const _capFirst = (s) => {
    if (!s) return s;
    const str = String(s);
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  // Construire le label : priorité 1 : activite_precise (libellé libre choisi par l'utilisateur, affichage uniquement)
  //                     priorité 2 : activite_autre (si sous_profil = "autre_*", legacy)
  //                     priorité 3 : label du sous-profil
  //                     priorité 4 : label du profil
  const activitePrecise = (property.activite_precise || property.taxonomy?.activite_precise || '').trim();
  if (activitePrecise) {
    return _capFirst(activitePrecise);
  }
  const isAutre = sous_profil && String(sous_profil).toLowerCase().startsWith('autre');
  const activiteAutre = (property.activite_autre || property.taxonomy?.activite_autre || '').trim();
  if (isAutre && activiteAutre) {
    return _capFirst(activiteAutre);
  }
  
  if (sous_profil && sousProfilLabels[sous_profil]) {
    return sousProfilLabels[sous_profil];
  }
  
  if (profil && profilLabels[profil]) {
    return profilLabels[profil];
  }
  
  return 'N/C';
}

/**
 * DEPRECATED: Utiliser getTitreMetier() à la place
 */
export function getTypeBien(property) {
  return getTitreMetier(property);
}

/**
 * Obtenir le loyer mensuel (bail)
 * @param {Object} property
 * @returns {number|null}
 */
export function getLoyerMensuel(property) {
  if (!property) return null;
  
  const infoBail = property.informations_bail || {};
  const honoraires = property.honoraires_calcul || {};
  
  if (infoBail.loyer_mensuel) return infoBail.loyer_mensuel;
  if (infoBail.loyer_annuel) return infoBail.loyer_annuel / 12;
  if (honoraires.loyer_mensuel) return honoraires.loyer_mensuel;
  
  return null;
}

/**
 * Obtenir le loyer du bail mensuel (pour cession/droit au bail)
 * @param {Object} property
 * @returns {number|null}
 */
export function getLoyerBailMensuel(property) {
  if (!property) return null;
  
  const infoBail = property.informations_bail || {};
  const honoraires = property.honoraires_calcul || {};
  
  if (infoBail.loyer_mensuel) return infoBail.loyer_mensuel;
  if (infoBail.loyer_annuel) return infoBail.loyer_annuel / 12;
  if (honoraires.loyer_mensuel) return honoraires.loyer_mensuel;
  
  return null;
}

/**
 * Obtenir le loyer du bail annuel
 * @param {Object} property
 * @returns {number|null}
 */
export function getLoyerBailAnnuel(property) {
  if (!property) return null;
  
  const infoBail = property.informations_bail || {};
  const honoraires = property.honoraires_calcul || {};
  
  if (infoBail.loyer_annuel) return infoBail.loyer_annuel;
  if (infoBail.loyer_mensuel) return infoBail.loyer_mensuel * 12;
  if (honoraires.loyer_annuel) return honoraires.loyer_annuel;
  
  return null;
}

/**
 * Obtenir la redevance mensuelle (location-gérance)
 * @param {Object} property
 * @returns {number|null}
 */
export function getRedevanceMensuelle(property) {
  if (!property) return null;
  
  const infoBail = property.informations_bail || {};
  
  if (infoBail.redevance_mensuelle) return infoBail.redevance_mensuelle;
  if (infoBail.redevance_annuelle) return infoBail.redevance_annuelle / 12;
  
  return null;
}

/**
 * Obtenir le loyer existant (murs loués, investissement)
 * @param {Object} property
 * @returns {number|null} - Loyer annuel existant
 */
export function getLoyerExistant(property) {
  if (!property) return null;
  
  const infoBail = property.informations_bail || {};
  const honoraires = property.honoraires_calcul || {};
  
  // Priorité : informations_bail (nouveau format)
  if (infoBail.loyer_annuel) return infoBail.loyer_annuel;
  if (infoBail.loyer_mensuel) return infoBail.loyer_mensuel * 12;
  
  // Fallback legacy
  if (honoraires.loyer_annuel_actuel) return honoraires.loyer_annuel_actuel;
  
  return null;
}

/**
 * Obtenir le prix de cession (fonds de commerce, droit au bail)
 * @param {Object} property
 * @returns {number|null}
 */
export function getPrixCession(property) {
  if (!property) return null;
  
  const infoVente = property.informations_vente || {};
  const honoraires = property.honoraires_calcul || {};
  
  if (infoVente.prix_cession) return infoVente.prix_cession;
  if (honoraires.prix_vente) return honoraires.prix_vente;
  
  return null;
}

/**
 * Obtenir le prix de vente (immobilier)
 * @param {Object} property
 * @returns {number|null}
 */
export function getPrixVente(property) {
  if (!property) return null;
  
  const infoVente = property.informations_vente || {};
  const honoraires = property.honoraires_calcul || {};

  // iter130 — informations_vente prime ; honoraires_calcul n'est qu'un repli legacy
  if (infoVente.prix_cession) return infoVente.prix_cession;
  if (infoVente.net_vendeur) return infoVente.net_vendeur;
  if (honoraires.prix_vente) return honoraires.prix_vente;

  return null;
}

/**
 * Obtenir le CA annuel
 * @param {Object} property
 * @returns {number|null}
 */
export function getCAnnuel(property) {
  if (!property) return null;
  
  const infoConf = property.informations_confidentielles || {};
  const donneesFinancieres = property.donnees_financieres || {};
  const honoraires = property.honoraires_calcul || {};
  
  // Chercher dans les différents emplacements possibles
  if (infoConf.ca_n1) return infoConf.ca_n1;
  if (infoConf.chiffre_affaires_annuel) return infoConf.chiffre_affaires_annuel;
  if (infoConf.ca_n_moins_1) return infoConf.ca_n_moins_1;
  if (donneesFinancieres.ca_annuel) return donneesFinancieres.ca_annuel;
  if (donneesFinancieres.ca_n1) return donneesFinancieres.ca_n1;
  if (honoraires.ca_annuel) return honoraires.ca_annuel;
  
  return null;
}

/**
 * Obtenir l'EBE
 * @param {Object} property
 * @returns {number|null}
 */
export function getEBE(property) {
  if (!property) return null;
  
  const infoConf = property.informations_confidentielles || {};
  const honoraires = property.honoraires_calcul || {};
  
  if (infoConf.ebe) return infoConf.ebe;
  if (infoConf.ebe_retraite) return infoConf.ebe_retraite;
  if (honoraires.ebe) return honoraires.ebe;
  if (honoraires.ebe_retraite) return honoraires.ebe_retraite;
  
  return null;
}

/**
 * Obtenir la valeur d'un champ dynamique selon sa clé
 * @param {Object} property - Bien complet
 * @param {string} fieldKey - Clé du champ (ex: 'ca_annuel', 'loyer_mensuel')
 * @returns {any} - Valeur du champ ou null
 */
export function getFieldValue(property, fieldKey) {
  if (!property || !fieldKey) return null;
  
  const infoVente = property.informations_vente || {};
  const infoBail = property.informations_bail || {};
  const infoConf = property.informations_confidentielles || {};
  const donneesFinancieres = property.donnees_financieres || {};
  const infoGerance = property.location_gerance || {};
  const composition = property.composition || {};
  const honoraires = property.honoraires_calcul || {};
  
  // Mapping des clés vers les chemins de données
  const fieldMap = {
    // Prix
    'prix_cession': getPrixCession(property),
    'prix_annonce': infoVente.prix_annonce,
    'prix_vente': getPrixVente(property),
    'prix_net_vendeur': honoraires.net_vendeur,
    'honoraires_pourcentage': infoVente.honoraires_pourcentage,
    
    // Droit d'entrée (pas-de-porte)
    'droit_entree': getDroitEntree(property),
    
    // Loyers
    'loyer_mensuel': getLoyerMensuel(property),
    'loyer_bail_mensuel': getLoyerBailMensuel(property),
    'loyer_bail_annuel': getLoyerBailAnnuel(property),
    'loyer_annuel': infoBail.loyer_annuel,
    'loyer_existant_annuel': getLoyerExistant(property),
    'loyer_existant_mensuel': getLoyerExistant(property) ? getLoyerExistant(property) / 12 : null,
    'charges_mensuelles': infoBail.charges_mensuelles || infoBail.charges,
    'charges': infoBail.charges_mensuelles || infoBail.charges,
    'loyer_charges_incluses': infoBail.loyer_mensuel && infoBail.charges_mensuelles ? infoBail.loyer_mensuel + infoBail.charges_mensuelles : null,
    'redevance_mensuelle': getRedevanceMensuelle(property),
    'redevance': getRedevanceMensuelle(property),
    
    // Financiers
    'ca_annuel': getCAnnuel(property),
    'ca_n_moins_1': donneesFinancieres.ca_n_moins_1,
    'ebe': getEBE(property),
    'ebe_retraite': donneesFinancieres.ebe_retraite || donneesFinancieres.ebe,
    'resultat_net': donneesFinancieres.resultat_net,
    
    // Surface
    'surface': getSurfaceValue(property),
    'surface_totale': composition.superficie_totale || composition.surface_totale,
    'activite': property.activite || property.subcategory,
    
    // Bail
    'date_debut_bail': infoBail.date_debut_bail,
    'duree_bail': infoBail.duree_bail,
    'type_bail': infoBail.type_bail,
    'depot_garantie': infoBail.depot_garantie,
    'charges_mensuelles': infoBail.charges_mensuelles,
    'loyer_charges_incluses': infoBail.loyer_mensuel && infoBail.charges_mensuelles ? infoBail.loyer_mensuel + infoBail.charges_mensuelles : null,
    
    // Gérance
    'redevance_mensuelle': getRedevanceMensuelle(property),
    'duree_gerance': infoGerance.duree_gerance,
    'caution_gerance': infoGerance.caution_gerance,
    
    // Murs
    'loyer_existant_annuel': getLoyerExistant(property),
    'rendement': honoraires.rendement_brut,
    'rendement_brut': honoraires.rendement_brut,
    'charges_copro': donneesFinancieres.charges_copro,
    'taxe_fonciere': donneesFinancieres.taxe_fonciere,
    'nom_locataire': infoBail.nom_locataire,
    'activite_locataire': infoBail.activite_locataire,
    'duree_bail_restante': infoBail.duree_bail_restante,
    'date_fin_bail': infoBail.date_fin_bail,
    
    // Composition
    'nb_bureaux': composition.nb_bureaux,
    'nb_parkings': composition.nb_parkings,
    'etage': composition.etage,
    
    // Confidentiel
    'proprietaire': getProprietaire(property),
    'tel_proprietaire': getTelProprietaire(property),
    'email_proprietaire': getEmailProprietaire(property),
    'adresse_complete': getAdresseComplete(property),
    
    // Calculs
    'prix_m2': getPrixValue(property) && getSurfaceValue(property) ? getPrixValue(property) / getSurfaceValue(property) : null,
    'prix_ou_loyer': getPrixValue(property)
  };
  
  return fieldMap[fieldKey] !== undefined ? fieldMap[fieldKey] : null;
}

/**
 * Obtenir le nom du propriétaire
 * @param {Object} property
 * @returns {string|null}
 */
export function getProprietaire(property) {
  if (!property) return null;
  
  // Nouvelle structure
  if (property.mandat?.proprietaire) {
    const proprio = property.mandat.proprietaire;
    if (typeof proprio === 'string') return proprio;
    if (proprio.nom) return proprio.nom;
  }
  
  // Legacy : contact/owner
  if (property.contact?.name) return property.contact.name;
  if (property.owner?.name) return property.owner.name;
  if (property.proprietaire) return property.proprietaire;
  
  return null;
}

/**
 * Obtenir le téléphone du propriétaire
 * @param {Object} property
 * @returns {string|null}
 */
export function getTelProprietaire(property) {
  if (!property) return null;
  
  // Nouvelle structure
  if (property.mandat?.proprietaire?.telephone) {
    return property.mandat.proprietaire.telephone;
  }
  
  // Legacy
  if (property.contact?.phone) return property.contact.phone;
  if (property.owner?.phone) return property.owner.phone;
  if (property.tel_proprietaire) return property.tel_proprietaire;
  
  return null;
}

/**
 * Obtenir l'email du propriétaire
 * @param {Object} property
 * @returns {string|null}
 */
export function getEmailProprietaire(property) {
  if (!property) return null;
  
  // Nouvelle structure
  if (property.mandat?.proprietaire?.email) {
    return property.mandat.proprietaire.email;
  }
  
  // Legacy
  if (property.contact?.email) return property.contact.email;
  if (property.owner?.email) return property.owner.email;
  if (property.email_proprietaire) return property.email_proprietaire;
  
  return null;
}

/**
 * Obtenir l'adresse complète du bien
 * @param {Object} property
 * @returns {string|null}
 */
export function getAdresseComplete(property) {
  if (!property) return null;
  
  // Nouvelle structure
  const emplacement = property.emplacement || {};
  if (emplacement.adresse_complete) {
    return emplacement.adresse_complete;
  }
  
  // Construire l'adresse à partir des champs
  const parts = [];
  if (emplacement.adresse || emplacement.rue) parts.push(emplacement.adresse || emplacement.rue);
  if (emplacement.code_postal) parts.push(emplacement.code_postal);
  if (emplacement.ville) parts.push(emplacement.ville);
  
  if (parts.length > 0) return parts.join(', ');
  
  // Legacy
  if (property.address) return property.address;
  if (property.adresse_complete) return property.adresse_complete;
  
  return null;
}

/**
 * Obtenir un titre descriptif du bien basé sur la taxonomy
 * @param {Object} property
 * @returns {string} - Ex: "Restaurant à Nice", "Local commercial à louer", "Murs commerciaux"
 */
export function getPropertyTitle(property) {
  if (!property) return 'Bien (Legacy)';
  
  // Priorité 1: Titre de l'annonce personnalisé
  const titreAnnonce = property.bien_general?.titre_annonce;
  if (titreAnnonce && titreAnnonce.trim()) {
    return titreAnnonce.trim();
  }
  
  const taxonomy = property.taxonomy || {};
  const operation = taxonomy.operation || property.operation || property.transaction_type;
  const objet = taxonomy.objet || property.objet;
  const profil = taxonomy.profil || property.profil;
  const activite = property.activite || property.subcategory;
  
  // Mapping des objets vers des libellés lisibles
  const objetLabels = {
    'fonds_de_commerce': 'Fonds de commerce',
    'local_commercial': 'Local commercial',
    'murs_commerciaux': 'Murs commerciaux',
    'bureaux': 'Bureaux',
    'terrain': 'Terrain',
    'droit_au_bail': 'Droit au bail',
    'entrepot': 'Entrepôt',
    'immeuble': 'Immeuble'
  };
  
  // Mapping des profils vers des libellés lisibles
  const profilLabels = {
    'chr': 'CHR',
    'retail': 'Commerce',
    'investissement': 'Investissement',
    'immeuble_rapport': 'Immeuble de rapport'
  };
  
  // Mapping des opérations
  const operationLabels = {
    'cession': 'Cession',
    'vente_immo': 'Vente',
    'location': 'Location',
    'location_gerance': 'Location-gérance'
  };
  
  // Construction du titre descriptif
  let title = '';
  
  // Construire un titre riche selon la classification
  // Ex: "Murs commerciaux loués", "Restaurant à céder", "Local commercial en location"
  
  // 1. Base du titre : Activité > Objet > Profil
  let base = '';
  if (activite && activite !== 'N/C') {
    base = activite;
  } else if (objet && objetLabels[objet]) {
    base = objetLabels[objet];
  } else if (profil && profilLabels[profil]) {
    base = profilLabels[profil];
  }
  
  // 2. Contexte opérationnel selon operation
  if (base) {
    // Cas spéciaux : Murs loués avec rendement, Fonds à céder, etc.
    if (objet === 'murs_commerciaux' && operation === 'vente_immo') {
      title = 'Murs commerciaux loués';
      // Ajouter "avec rendement" si applicable
      const rendement = property.honoraires_calcul?.rendement_brut;
      if (rendement) title += ' avec rendement locatif';
    }
    else if (objet === 'fonds_de_commerce' && operation === 'cession') {
      title = `${base} à céder`;
    }
    else if (objet === 'droit_au_bail' && operation === 'cession') {
      title = `${base} à céder`;
    }
    else if (operation === 'location') {
      title = `${base} en location`;
    }
    else if (operation === 'location_gerance') {
      title = `${base} en location-gérance`;
    }
    else if (operation === 'vente_immo') {
      title = `${base} à vendre`;
    }
    else {
      // Fallback simple
      title = base;
    }
  }
  // 3. Fallback ultime si pas de base
  else if (operation && operationLabels[operation]) {
    title = `Bien en ${operationLabels[operation].toLowerCase()}`;
  }
  else {
    // Legacy : utiliser catégorie ou sous-catégorie si disponible
    title = property.category || property.subcategory || `Bien (Réf: ${property.id?.slice(0, 8) || 'NC'})`;
  }
  
  return title;
}

export default {
  getPrixValue,
  getPrixLabel,
  getSurfaceValue,
  getVille,
  getSecteur,
  getTypeBien,
  getLoyerMensuel,
  getDroitEntree,
  getCAnnuel,
  getEBE,
  getFieldValue,
  getPropertyTitle,
  getProprietaire,
  getTelProprietaire,
  getEmailProprietaire,
  getAdresseComplete,
  // Nouveaux getters spécifiques
  getLoyerBailMensuel,
  getLoyerBailAnnuel,
  getRedevanceMensuelle,
  getLoyerExistant,
  getPrixCession,
  getPrixVente
};
